import { useMemo, useState } from "react";
import { useTaskHistory } from "../api/hooks";
import { MONTHS_LONG, MONTHS_SHORT, WEEKDAYS_SHORT, absMinutes, addDays, addMonths, diffDays, nowIn, parts, startOfMonth, startOfWeek, weekday, type DateStr } from "../lib/date";
import { useUi } from "../state/ui";

type Period = "24h" | "3d" | "7d" | "30d" | "all";

const PERIODS: Array<{ key: Period; label: string; lead: string }> = [
  { key: "24h", label: "24 h", lead: "nas últimas 24 horas" },
  { key: "3d", label: "3 dias", lead: "nos últimos 3 dias" },
  { key: "7d", label: "7 dias", lead: "nos últimos 7 dias" },
  { key: "30d", label: "30 dias", lead: "nos últimos 30 dias" },
  { key: "all", label: "Tudo", lead: "desde o começo" }
];

/** A column: [start, end) in absolute local minutes, its axis tick and its tooltip label. */
interface Bucket {
  start: number;
  end: number;
  axis: string;
  tip: string;
}

const hh = (h: number) => `${String(h).padStart(2, "0")}h`;
const dayShort = (d: DateStr) => `${WEEKDAYS_SHORT[weekday(d)]}, ${parts(d).d} de ${MONTHS_SHORT[parts(d).m - 1]}`;
const dayAbs = (d: DateStr) => absMinutes(`${d}T00:00`);

function dailyBuckets(first: DateStr, count: number, axis: (d: DateStr) => string): Bucket[] {
  return Array.from({ length: count }, (_, i) => {
    const d = addDays(first, i);
    return { start: dayAbs(d), end: dayAbs(d) + 1440, axis: axis(d), tip: dayShort(d) };
  });
}

/** Columns for a period, ending at the current hour / day (all in the user's wall clock). */
function bucketsFor(period: Period, today: DateStr, nowMinutes: number, since: DateStr, weekStart: number): Bucket[] {
  const todayAbs = dayAbs(today);
  switch (period) {
    case "24h": {
      const lastHour = todayAbs + Math.floor(nowMinutes / 60) * 60;
      return Array.from({ length: 24 }, (_, i) => {
        const start = lastHour - (23 - i) * 60;
        const h = ((start % 1440) + 1440) % 1440 / 60;
        return { start, end: start + 60, axis: hh(h), tip: `${hh(h)}–${hh((h + 1) % 24)}${start < todayAbs ? " · ontem" : ""}` };
      });
    }
    case "3d": {
      const lastSlot = todayAbs + Math.floor(nowMinutes / 360) * 360;
      return Array.from({ length: 12 }, (_, i) => {
        const start = lastSlot - (11 - i) * 360;
        const day = addDays(today, Math.floor((start - todayAbs) / 1440));
        const h = (start - dayAbs(day)) / 60;
        return { start, end: start + 360, axis: h === 0 ? WEEKDAYS_SHORT[weekday(day)] : "", tip: `${dayShort(day)} · ${hh(h)}–${hh((h + 6) % 24)}` };
      });
    }
    case "7d":
      return dailyBuckets(addDays(today, -6), 7, (d) => WEEKDAYS_SHORT[weekday(d)]);
    case "30d":
      return dailyBuckets(addDays(today, -29), 30, (d) => `${parts(d).d}/${parts(d).m}`);
    case "all": {
      const first = since < today ? since : today;
      const days = diffDays(first, today) + 1;
      // Daily up to a month (at least a week of columns), then weekly, then monthly.
      if (days <= 31) {
        const span = Math.max(days, 7);
        return dailyBuckets(addDays(today, 1 - span), span, (d) => `${parts(d).d}/${parts(d).m}`);
      }
      if (days <= 26 * 7) {
        const out: Bucket[] = [];
        for (let w = startOfWeek(first, weekStart); w <= today; w = addDays(w, 7)) {
          out.push({ start: dayAbs(w), end: dayAbs(w) + 7 * 1440, axis: `${parts(w).d}/${parts(w).m}`, tip: `Semana de ${parts(w).d} de ${MONTHS_SHORT[parts(w).m - 1]}` });
        }
        return out;
      }
      const out: Bucket[] = [];
      for (let m = startOfMonth(first); m <= today; m = addMonths(m, 1)) {
        const { y, m: mm } = parts(m);
        out.push({ start: dayAbs(m), end: dayAbs(addMonths(m, 1)), axis: MONTHS_SHORT[mm - 1].replace(".", ""), tip: `${MONTHS_LONG[mm - 1]} de ${y}` });
      }
      return out;
    }
  }
}

function plural(n: number, one: string, many: string) {
  return `${n} ${n === 1 ? one : many}`;
}

/** Completed tasks over time: hero total for the period, then one column per bucket. */
export function TaskChart() {
  const ui = useUi();
  const { data } = useTaskHistory();
  const [period, setPeriod] = useState<Period>("7d");
  const [hover, setHover] = useState<number | null>(null);
  const tz = ui.me.timezone;

  const completions = useMemo(
    () =>
      (data?.completions ?? []).map((iso) => {
        const local = nowIn(tz, new Date(iso));
        return dayAbs(local.date) + local.minutes;
      }),
    [data, tz]
  );
  const since = data ? nowIn(tz, new Date(data.since)).date : ui.today;
  const buckets = bucketsFor(period, ui.today, nowIn(tz).minutes, since, ui.me.settings.weekStart);
  const counts = buckets.map((b) => completions.filter((t) => t >= b.start && t < b.end).length);
  const total = counts.reduce((a, b) => a + b, 0);
  const max = Math.max(...counts, 0);
  const peak = max > 0 ? counts.indexOf(max) : -1;
  const tickEvery = Math.ceil(buckets.length / 7);
  const meta = PERIODS.find((p) => p.key === period)!;
  const active = hover ?? null;

  return (
    <section className="chart-card" aria-label="Tarefas concluídas ao longo do tempo">
      <div className="chart-head">
        <div className="chart-figure">
          <p className="chart-total">
            <span className="chart-number display">{total}</span>
            <span className="chart-lead">
              {total === 1 ? "tarefa concluída" : "tarefas concluídas"} {meta.lead}
            </span>
          </p>
        </div>
        <div className="seg" role="tablist" aria-label="Período">
          {PERIODS.map((p) => (
            <button key={p.key} role="tab" aria-selected={period === p.key} className={`seg-opt${period === p.key ? " on" : ""}`} onClick={() => setPeriod(p.key)}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="chart-plot" onPointerLeave={() => setHover(null)}>
        <div className="chart-cols" style={{ ["--cols" as string]: buckets.length }} aria-hidden>
          {buckets.map((b, i) => (
            <div
              key={b.start}
              className={`chart-col${active === i ? " hover" : ""}`}
              onPointerEnter={() => setHover(i)}
              onClick={() => setHover(i)}
            >
              {i === peak && active === null && <span className="chart-cap">{max}</span>}
              <span className="chart-bar" style={{ height: max ? `${(counts[i] / max) * 100}%` : 0 }} />
            </div>
          ))}
        </div>
        {active !== null && (
          <div
            className="chart-tip"
            role="status"
            style={{
              left: `${((active + 0.5) / buckets.length) * 100}%`,
              // Keep it inside the card near the edges.
              transform: `translate(${active < buckets.length * 0.2 ? "-16px" : active >= buckets.length * 0.8 ? "calc(-100% + 16px)" : "-50%"}, -100%)`
            }}
          >
            <strong>{plural(counts[active], "concluída", "concluídas")}</strong>
            <span>{buckets[active].tip}</span>
          </div>
        )}
        <div className="chart-axis" style={{ ["--cols" as string]: buckets.length }} aria-hidden>
          {buckets.map((b, i) => (
            <span key={b.start}>{(buckets.length - 1 - i) % tickEvery === 0 ? b.axis : ""}</span>
          ))}
        </div>
      </div>
      {!total && <p className="chart-empty">Nenhuma tarefa concluída neste período.</p>}

      <table className="visually-hidden">
        <caption>Tarefas concluídas {meta.lead}</caption>
        <thead>
          <tr>
            <th scope="col">Período</th>
            <th scope="col">Concluídas</th>
          </tr>
        </thead>
        <tbody>
          {buckets.map((b, i) => (
            <tr key={b.start}>
              <td>{b.tip}</td>
              <td>{counts[i]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
